import { makeStyles } from "@material-ui/core/styles";
import { Box, Grid, Skeleton, Snackbar } from "@mui/material";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import ResultsContext from "../../../../../contexts/ResultsContext";
import {
  dismissNotifications,
  fetchNotifications,
  notificationsSelector,
} from "../../../../../redux/slices/notifications";
import {
  // fetchBusinessReport,
  // fetchAdsBusinessReport,
  fetchOrdersBusinessReport,
  fetchSalesBusinessReport,
  productDetailSelector,
} from "../../../../../redux/slices/product_detail";
import {
  clearStoreSalesChartData,
  fetchStoreSalesChartData,
  getWeeklyInsights,
  storeAnalyticsSelector,
} from "../../../../../redux/slices/store_analytics";
import { trackEvent } from "../../../../../utils/analytics";
import tooltipContent from "../../../../components/tooltips_content";
import { GetCalculatedReportsData, roundNumber } from "../../helpers";
import WeeklyInsights from "../WeeklyInsights";
import { ChartLoader } from "../chartloader";
import DatePicker from "../shared/DatePicker";

import DetailsCard from "./../shared/DetailsCard";
import Alerts from "./Alert";
import GoalsKpis from "./GoalsKpis";
import GraphCard from "./GraphCard";
import SummaryCard from "./SummaryCard";
import { LineChart } from "./chart";

const DateRangeWrapper = styled.div``;
const DateLabel = styled.div`
  margin-left: 15px;
  font-size: 15px;
  line-height: 15px;
  color: #808080;
  font-weight: 500;
`;

const Sales = styled.div`
  margin-left: 15px;
  font-size: 12px;
  line-height: 12px;
  margin-top: 15px;
  font-weight: 600;
`;
const GraphCardsContainer = styled(Box)`
  position: relative;
  display: flex;
  justify-content: space-between;
  margin: 3% 0;
`;

const Title = styled.label`
  font-weight: 700;
  margin-bottom: 20px;
  font-size: 18px;
  margin: 10px 0;
`;

const DatePickerGrid = styled(Grid)`
  display: flex;
  justify-content: flex-end;
  align-self: flex-end;
`;

const useStyles = makeStyles({
  styleRangeButton: {
    justifyContent: "end",
  },
  gridStyle: {
    marginTop: "20px",
    marginBottom: "40px",
  },
  performanceStyle: {
    marginRight: "40px",
    position: "relative",
  },
});

function StoreAnalysis({ storeName }) {
  const { storeAnalyticsData } = useContext(ResultsContext);
  // const [timeRange, setTimeRange] = useState({});
  const dispatch = useDispatch();

  // Helper function to format extra fees description for tooltip
  const formatExtraFeesDesc = (extraFeesDesc) => {
    if (!extraFeesDesc) {
      return "";
    }

    // Helper to truncate text to 80 chars
    const truncate = (text, maxLength = 80) => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength - 3) + "...";
    };

    // Convert to array if it's an object
    const feesArray = Array.isArray(extraFeesDesc)
      ? extraFeesDesc
      : Object.values(extraFeesDesc);

    if (feesArray.length === 0) {
      return "";
    }

    // Group entries by category (second line)
    const grouped = {};
    const categoryTotals = {};

    feesArray.forEach((fee) => {
      // Array structure: [amount, description, category]
      let amount, description, category;

      if (Array.isArray(fee) && fee.length >= 3) {
        amount = fee[0]; // Amount
        description = fee[1]; // Description to group by (e.g., "Adjustment")
        category = fee[2]; // Category (the detail item)
      } else if (Array.isArray(fee) && fee.length === 2) {
        amount = fee[0];
        description = fee[1];
        category = "Other";
      } else if (typeof fee === "object" && fee !== null) {
        // Fallback for object format
        description =
          fee.name || fee.line1 || fee.description || fee.type || "Other";
        category = fee.category || fee.line2 || fee.group || fee.fee_type || "";
        amount = fee.amount || fee.value || fee.fee || 0;
      } else {
        // Fallback for other formats
        description = "Other";
        category = String(fee);
        amount = 0;
      }

      // Group by description (fee[1])
      if (!grouped[description]) {
        grouped[description] = [];
        categoryTotals[description] = 0;
      }

      // Add to group total if numeric
      if (typeof amount === "number") {
        categoryTotals[description] += amount;
      }

      // Store as object for sorting later
      grouped[description].push({
        amount: amount,
        category: category,
      });
    });

    // Format grouped data with totals, sorted by absolute value descending
    return Object.entries(grouped)
      .sort(([catA], [catB]) => {
        const absA = Math.abs(categoryTotals[catA]);
        const absB = Math.abs(categoryTotals[catB]);
        return absB - absA; // Sort descending by absolute value
      })
      .map(([category, items]) => {
        // Sort items within group by absolute value descending
        const sortedItems = items.sort((a, b) => {
          return Math.abs(b.amount) - Math.abs(a.amount);
        });

        // Format all values to find max width
        const formattedItems = sortedItems.map((item) => {
          const formattedValue =
            typeof item.amount === "number"
              ? item.amount < 0
                ? `-$${Math.abs(item.amount).toLocaleString()}`
                : `+$${item.amount.toLocaleString()}`
              : item.amount;
          return { value: formattedValue, category: item.category };
        });

        // Format items with descriptions using tabs for better alignment
        const formattedLines = formattedItems.map((item) => {
          // Truncate category
          const truncatedCategory =
            item.category.length > 65
              ? item.category.substring(0, 62) + "..."
              : item.category;
          // Use tabs for alignment (works better in native tooltips)
          const line = `${item.value}\t${truncatedCategory}`;
          return line;
        });

        const total = categoryTotals[category];
        const formattedTotal =
          total < 0
            ? `-$${Math.abs(total).toLocaleString()}`
            : `+$${total.toLocaleString()}`;
        const categoryHeader = truncate(
          `${category} (Total: ${formattedTotal})`
        );
        return `${categoryHeader}:\n${formattedLines.join("\n")}`;
      })
      .join("\n\n");
  };

  const {
    businessReportData,
    salesBusinessReportLoading,
    ordersBusinessReportLoading,
    // adsBusinessReportLoading,
  } = useSelector(productDetailSelector);

  const { notifications, notificationsLoading } = useSelector(
    notificationsSelector
  );

  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  const [compareStartDate, setCompareStartDate] = useState(
    moment().subtract(1, "years").startOf("month").format("YYYY-MM-DD")
  );
  const [compareEndDate, setCompareEndDate] = useState(
    moment().subtract(1, "years").format("YYYY-MM-DD")
  );

  const [calculatedReportsData, setCalculatedReportsData] = useState({});

  const store = storeName ? storeName.replace("_client", "") : storeName;

  useEffect(() => {
    dispatch(
      fetchSalesBusinessReport({
        store: store,
        start_date: startDate,
        end_date: endDate,
        compare_start_date: compareStartDate,
        compare_end_date: compareEndDate,
      })
    );
    dispatch(
      fetchOrdersBusinessReport({
        store: store,
        start_date: startDate,
        end_date: endDate,
        compare_start_date: compareStartDate,
        compare_end_date: compareEndDate,
      })
    );
    // dispatch(
    //   fetchAdsBusinessReport({
    //     store: storeName,
    //     start_date: startDate,
    //     end_date: endDate,
    //     compare_start_date: compareStartDate,
    //     compare_end_date: compareEndDate,
    //   })
    // );
    dispatch(fetchNotifications({ store: store }));
    if (storeName !== "all" && storeName !== null) {
      dispatch(getWeeklyInsights(store));
    }

    let salesChartDataDispatch = {
      store: store,
      start_date: startDate,
      end_date: endDate,
    };
    if (compareStartDate && compareEndDate) {
      salesChartDataDispatch = {
        ...salesChartDataDispatch,
        compare_start_date: compareStartDate,
        compare_end_date: compareEndDate,
      };
    }
    if (storeName !== "all") {
      dispatch(fetchStoreSalesChartData(salesChartDataDispatch));
    } else {
      dispatch(clearStoreSalesChartData());
    }
  }, [
    dispatch,
    startDate,
    endDate,
    compareStartDate,
    compareEndDate,
    storeName,
    store,
  ]);

  useEffect(() => {
    setCalculatedReportsData(GetCalculatedReportsData(businessReportData));
  }, [businessReportData]);

  // Graph cards
  const {
    storeSalesChartData,
    storeSalesChartLoading,
    weeklyInsightData,
    weeklyInsightLoading,
  } = useSelector(storeAnalyticsSelector);

  const graphDefaultMetrics = { sales: 0, units: 0, date_range: "" };
  const [chosenPeriod, setChoosenPeriod] = useState(graphDefaultMetrics);
  const [lastPeriod, setLastPeriod] = useState(graphDefaultMetrics);
  const [samePeriodLastYear, setSamePeriodLastYear] =
    useState(graphDefaultMetrics);
  useEffect(() => {
    if (storeSalesChartData?.middle_cards) {
      const { chosen_period, last_period, same_period_last_year } =
        storeSalesChartData.middle_cards;
      setChoosenPeriod(chosen_period);
      setLastPeriod(last_period);
      setSamePeriodLastYear(same_period_last_year);
    } else {
      setChoosenPeriod({ sales: 0, units: 0, date_range: "" });
      setLastPeriod({ sales: 0, units: 0, date_range: "" });
      setSamePeriodLastYear({ sales: 0, units: 0, date_range: "" });
    }
  }, [storeSalesChartData]);

  // alert notifications
  const [availableAlerts, setAvailableAlerts] = useState([]);
  useEffect(() => {
    const alerts = storeAnalyticsData.alerts || [];
    const availableAlerts = [];
    alerts.forEach((item) => {
      if (
        item.type === "Account deactivated" ||
        item.type === "Listing deactivated" ||
        item.type === "PPC credit card not working" ||
        item.type === "Out of stock" ||
        item.type === "Poor/very poor voice of the customer"
      ) {
        availableAlerts.push(item);
      }
    });
    setAvailableAlerts(availableAlerts);
  }, [storeAnalyticsData]);

  const [storeSelectAlert, setStoreSelectAlert] = useState(false);
  const onDatesSelected = useCallback(
    (startDate, endDate, comparedStartDate, comparedEndDate) => {
      trackEvent("date_range_change", {
        section: "store_analysis",
        store: storeName,
        start_date: startDate
          ? new Date(startDate).toISOString().split("T")[0]
          : "",
        end_date: endDate ? new Date(endDate).toISOString().split("T")[0] : "",
      });
      if (storeName === "all") {
        setStoreSelectAlert(true);

        setTimeout(() => {
          setStoreSelectAlert(false);
        }, 2000);
      }

      const payload = {
        start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
        end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
        compare_start_date: comparedStartDate
          ? moment(comparedStartDate).format("YYYY-MM-DD")
          : "",
        compare_end_date: comparedEndDate
          ? moment(comparedEndDate).format("YYYY-MM-DD")
          : "",
      };

      // setTimeRange(payload);

      //setDateRange(
      //  `${moment(startDate).format("MMM DD YYYY")} - ${moment(endDate).format(
      //    "MMM DD YYYY"
      //  )}`
      //);

      if (payload["start_date"] && payload["end_date"]) {
        setStartDate(payload["start_date"]);
        setEndDate(payload["end_date"]);
      }
      setCompareStartDate(payload["compare_start_date"]);
      setCompareEndDate(payload["compare_end_date"]);
    },
    [storeName]
  );

  const classes = useStyles();

  // Dismiss single notification
  const dismissNotification = useCallback(
    (notification_id) => {
      trackEvent("alert_dismissed", { store: storeName, type: "single" });
      dispatch(dismissNotifications({ notification_id, store }));
    },
    [dispatch, storeName, store]
  );

  const dismissAllNotification = useCallback(() => {
    trackEvent("alert_dismissed", {
      store: storeName,
      type: "all",
      count: notifications.length,
    });
    dispatch(dismissNotifications({ store }));
  }, [dispatch, storeName, store, notifications.length]);

  return (
    <React.Fragment>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={storeSelectAlert}
        message="Please select a store first!"
      />
      <Grid container>
        <Grid item xs={6}>
          <GoalsKpis />
        </Grid>
        <DatePickerGrid item xs={6}>
          <DatePicker
            onDatesSelected={onDatesSelected}
            defaultDates={{
              startDate: new Date(startDate),
              endDate: new Date(endDate),
              compareStartDate: new Date(compareStartDate),
              compareEndDate: new Date(compareEndDate),
            }}
            defaultPreset={"Month to date"}
            disabled={
              storeSalesChartLoading ||
              salesBusinessReportLoading ||
              ordersBusinessReportLoading
            }
          />
        </DatePickerGrid>
      </Grid>
      {availableAlerts.map((item, key) => (
        <Alerts key={key} alertsDetails={item} />
      ))}
      <Grid className={classes.gridStyle} container spacing={0.5}>
        <Grid item xs={3}>
          <SummaryCard
            dataTestId="sales_summary"
            title="Sales"
            value={`$ ${roundNumber(calculatedReportsData.sumOfSales)}`}
            percentage={roundNumber(calculatedReportsData.salesComparison)}
            subTitleOne="Net profit ($)"
            totalValueOne={`$${Number(
              calculatedReportsData.netProfit
            ).toLocaleString()}`}
            subTitleTwo="Net margin (%)"
            totalValueTwo={`${Number(
              calculatedReportsData.netMargin
            ).toLocaleString()}%`}
            content={tooltipContent.store_management_dashboard.sales}
            showCollapseValueOne={true}
            isLoading={salesBusinessReportLoading}
            valueOneExpended={`Amazon US:
            <br>
            <div style="display: flex">
              <div style="width: 100%">&nbsp;Sales</div>
              <div>
              $${Number(calculatedReportsData.pnlRevenue).toLocaleString()}
              </div>
              <div style="text-align: right; width: 75px; font-size: 10px; font-style: italic;">
                (100%)&nbsp;&nbsp;
              </div>
            </div>
            <div style="display: flex">
              <div style="width: 100%">&nbsp;Ads</div>
              <div>
              -$${Number(calculatedReportsData.pnlPPCSpend).toLocaleString()}
              </div>
              <div style="text-align: right; width: 75px; font-size: 10px; font-style: italic;">&nbsp;(${
                Math.round(
                  (calculatedReportsData.pnlPPCSpend /
                    calculatedReportsData.pnlRevenue) *
                    100
                ) / 1
              }%)&nbsp;&nbsp;</div>
            </div>
            <div style="display: flex">
              <div style="width: 100%">&nbsp;Refunds</div>
              <div>
              -$${Number(calculatedReportsData.pnlRefundCost).toLocaleString()}
              </div>
              <div style="text-align: right; width: 75px; font-size: 10px; font-style: italic;">&nbsp;(${
                Math.round(
                  (calculatedReportsData.pnlRefundCost /
                    calculatedReportsData.pnlRevenue) *
                    100
                ) / 1
              }%)&nbsp;&nbsp;</div>
            </div>
            <div style="display: flex">
              <div style="width: 100%;">&nbsp;Promotions</div>
              <div>
              -$${Number(
                calculatedReportsData.pnlPromotionCost
              ).toLocaleString()}
              </div>
              <div style="text-align: right; width: 75px; font-size: 10px; font-style: italic;">&nbsp;(${
                Math.round(
                  (calculatedReportsData.pnlPromotionCost /
                    calculatedReportsData.pnlRevenue) *
                    100
                ) / 1
              }%)&nbsp;&nbsp;</div>
            </div>
            <div style="display: flex">
              <div style="width: 100%;">&nbsp;Fulfilment Fees</div>
              <div>
              -$${Number(
                calculatedReportsData.pnlFulfilmentFees
              ).toLocaleString()}
              </div>
              <div style="text-align: right; width: 75px; font-size: 10px; font-style: italic;">&nbsp;(${
                Math.round(
                  (calculatedReportsData.pnlFulfilmentFees /
                    calculatedReportsData.pnlRevenue) *
                    100
                ) / 1
              }%)&nbsp;&nbsp;</div>
            </div>
            <div style="display: flex">
              <div style="width: 100%;">&nbsp;Referral Fees</div>
              <div>
              -$${Number(
                calculatedReportsData.pnlReferralFees
              ).toLocaleString()}
              </div>
              <div style="text-align: right; width: 75px; font-size: 10px; font-style: italic;">&nbsp;(${
                Math.round(
                  (calculatedReportsData.pnlReferralFees /
                    calculatedReportsData.pnlRevenue) *
                    100
                ) / 1
              }%)&nbsp;&nbsp;</div>
            </div>
            <div style="display: flex">
              <div style="width: 100%;">
                <span 
                  style="cursor: help; text-decoration: underline dotted;" 
                  title="${formatExtraFeesDesc(
                    calculatedReportsData.pnlExtraFeesDesc
                  )}"
                >
                  &nbsp;Extra Fees
                </span>
              </div>
              <div>
              ${
                Number(calculatedReportsData.pnlExtraFees) < 0
                  ? "-$" +
                    (
                      Number(calculatedReportsData.pnlExtraFees) * -1
                    ).toLocaleString()
                  : "+$" +
                    Number(calculatedReportsData.pnlExtraFees).toLocaleString()
              }
              </div>
              <div style="text-align: right; width: 75px; font-size: 10px; font-style: italic;">&nbsp;(${
                Math.round(
                  (calculatedReportsData.pnlExtraFees /
                    calculatedReportsData.pnlRevenue) *
                    100
                ) / 1
              }%)&nbsp;&nbsp;</div>
            </div>
            <div style="display: flex">
              <div style="width: 100%;">&nbsp;COGS</div>
              <div>
              -$${Number(calculatedReportsData.pnlCogs).toLocaleString()}
              </div>
              <div style="text-align: right; width: 75px; font-size: 10px; font-style: italic;">&nbsp;(${
                Math.round(
                  (calculatedReportsData.pnlCogs /
                    calculatedReportsData.pnlRevenue) *
                    100
                ) / 1
              }%)&nbsp;&nbsp;</div>
            </div>
            ${
              calculatedReportsData.pnlVatAmount
                ? `
            <div style="display: flex">
              <div style="width: 100%;">&nbsp;VAT</div>
              <div>
              -$${Number(calculatedReportsData.pnlVatAmount).toLocaleString()}
              </div>
              <div style="text-align: right; width: 75px; font-size: 10px; font-style: italic;">&nbsp;(${
                Math.round(
                  (calculatedReportsData.pnlVatAmount /
                    calculatedReportsData.pnlRevenue) *
                    100
                ) / 1
              }%)&nbsp;&nbsp;</div>
            </div>
            `
                : ""
            }
            `}
          />
        </Grid>
        <Grid item xs={3}>
          <SummaryCard
            dataTestId="orders_summary"
            title="Orders"
            value={roundNumber(calculatedReportsData.sumOfOrders)}
            percentage={roundNumber(calculatedReportsData.ordersComparison)}
            // subTitleOne="Returns"
            // totalValueOne={`${Number(
            //   calculatedReportsData.returns
            // ).toLocaleString()}`}
            subTitleTwo="Units"
            totalValueTwo={`${Number(
              calculatedReportsData.units
            ).toLocaleString()}`}
            content={tooltipContent.store_management_dashboard.orders}
            strikeValueOne={!calculatedReportsData.returns}
            isLoading={ordersBusinessReportLoading}
          />
        </Grid>
        <Grid item xs={3}>
          <SummaryCard
            dataTestId="conversion_rate_summary"
            title="Conversion rate"
            value={roundNumber(calculatedReportsData.conversionRate) + "%"}
            percentage={roundNumber(
              calculatedReportsData.conversionRateComparison
            )}
            subTitleOne="Sessions"
            totalValueOne={`${Number(
              calculatedReportsData.sumOfSessions
            ).toLocaleString()}`}
            subTitleTwo="Page views"
            totalValueTwo={`${Number(
              calculatedReportsData.sumOfPageViews
            ).toLocaleString()}`}
            content={tooltipContent.store_management_dashboard.conversion_rate}
            isLoading={salesBusinessReportLoading}
          />
        </Grid>
        <Grid item xs={3}>
          <SummaryCard
            dataTestId="ads_spent_summary"
            title="Ads spent"
            value={`$${roundNumber(calculatedReportsData.sumOfSpent)}`}
            percentage={roundNumber(calculatedReportsData.adsSpentComparison)}
            subTitleOne="Tacos"
            totalValueOne={`${Number(
              calculatedReportsData.tacos
            ).toLocaleString()}%`}
            subTitleTwo="ROAS"
            totalValueTwo={`${Number(
              calculatedReportsData.roas
            ).toLocaleString()}`}
            content={tooltipContent.store_management_dashboard.ads_spent}
            isLoading={salesBusinessReportLoading}
          />
        </Grid>
      </Grid>
      <>
        <div className={classes.performanceStyle}>
          <DateRangeWrapper>
            <DateLabel>
              {moment(startDate).format("MMM DD YYYY")} -{" "}
              {moment(endDate).format("MMM DD YYYY")}
            </DateLabel>
            <Sales>Sales($)</Sales>
          </DateRangeWrapper>
          {storeSalesChartLoading ? (
            <>
              <ChartLoader />
            </>
          ) : (
            <LineChart />
          )}
        </div>
        <GraphCardsContainer>
          <GraphCard
            dataTestId="chosen_period_graph_card"
            title="Chosen Period"
            amount={Number(chosenPeriod.sales).toLocaleString()}
            units={chosenPeriod.units}
            accentColor="#3F73E5"
            active="true"
            date={chosenPeriod.date_range}
            isLoading={storeSalesChartLoading}
          />
          <GraphCard
            dataTestId="last_period_graph_card"
            title="Last Period"
            amount={Number(lastPeriod.sales).toLocaleString()}
            units={lastPeriod.units}
            accentColor="#A855F7"
            date={lastPeriod.date_range}
            isLoading={storeSalesChartLoading}
          />
          <GraphCard
            dataTestId="same_period_last_year_graph_card"
            title="Same Period Last Year"
            amount={Number(samePeriodLastYear.sales).toLocaleString()}
            units={samePeriodLastYear.units}
            accentColor="#F59E0B"
            date={samePeriodLastYear.date_range}
            isLoading={storeSalesChartLoading}
          />
        </GraphCardsContainer>
      </>
      <Grid className={classes.gridStyle} container spacing={2}>
        <Grid item xs={6}>
          <DetailsCard
            title="Listings"
            firstWarningTitle="Listings require attention"
            firstWarningValue={`${notifications.length} Notifications`}
            firstWarningDetails={`${notifications.length} Notifications`}
            isLoading={notificationsLoading}
            dismissNotification={dismissNotification}
            secondRowShow={false}
            notifications={notifications}
            // secondWarningTitle="Listings performing poor details"
            // secondWarningValue="2 listings worse than average"
            // secondWarningDetails="2 listings worse than average details"
            dismissAllNotification={dismissAllNotification}
          />
        </Grid>
      </Grid>
      <Title>Weekly Insights</Title>
      {weeklyInsightLoading ? (
        <>
          {[1, 2, 3, 4, 5].map((item) => (
            <Skeleton
              key={item}
              width={"97%"}
              sx={{ mt: 5, ml: 5 }}
              variant="rectangular"
              height={15}
            />
          ))}
        </>
      ) : (
        <WeeklyInsights weeklyInsightData={weeklyInsightData || []} />
      )}
    </React.Fragment>
  );
}

export default StoreAnalysis;
